import { useEffect, useState } from 'react';
import { Alert, Button, Col, Form, Modal, Row, Table } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';

import { formatDate, formatDateTime } from '../utils/dateFormat';
const show = (v, suffix = '') => (v == null || v === '' ? 'none' : `${v}${suffix}`);

/** System Admin: change an account manager's commission terms on one customer, from today. Past commissions keep their rate. */
function PartnerAssignmentTermsModal({ assignment, onHide, onSaved }) {
  const [form, setForm] = useState(null);
  const [changes, setChanges] = useState([]);
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState('');

  useEffect(() => {
    if (!assignment) { setForm(null); return; }
    setProblem('');
    setForm({
      initialRatePercent: String(assignment.initialRatePercent ?? ''),
      trailingRatePercent: String(assignment.trailingRatePercent ?? ''),
      initialPeriodMonths: String(assignment.initialPeriodMonths ?? ''),
      capAmount: assignment.capAmount != null ? String(assignment.capAmount) : '',
      capMonths: assignment.capMonths != null ? String(assignment.capMonths) : ''
    });
    axios.get(`/api/system-admin/partner-assignments/${assignment.id}/changes`)
      .then((r) => setChanges(Array.isArray(r.data) ? r.data : []))
      .catch(() => setChanges([]));
  }, [assignment]);

  if (!assignment || !form) return null;

  const set = (name) => (e) => setForm({ ...form, [name]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setProblem('');
    try {
      await axios.put(`/api/system-admin/partner-assignments/${assignment.id}`, {
        initialRatePercent: form.initialRatePercent,
        trailingRatePercent: form.trailingRatePercent,
        initialPeriodMonths: form.initialPeriodMonths,
        capAmount: form.capAmount === '' ? null : form.capAmount,
        capMonths: form.capMonths === '' ? null : form.capMonths
      });
      toast.success('Terms changed from today');
      if (onSaved) onSaved();
      onHide();
    } catch (error) {
      const data = error.response?.data;
      setProblem(typeof data === 'string' ? data : 'Could not change the terms');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show onHide={onHide} size="lg">
      <Form onSubmit={save}>
        <Modal.Header closeButton>
          <Modal.Title>Change terms: {assignment.partner?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info" className="py-2 small">
            The new terms apply to payments from today. Commissions already recorded keep the rate they were earned at, and the
            start date ({assignment.startDate ? formatDate(assignment.startDate) : ''}) does not change.
          </Alert>
          {problem && <Alert variant="danger" className="py-2">{problem}</Alert>}
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3" controlId="pat-initial-rate">
                <Form.Label>Rate in the first period (%)</Form.Label>
                <Form.Control type="number" min="0" max="100" step="0.01" required value={form.initialRatePercent} onChange={set('initialRatePercent')} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3" controlId="pat-initial-months">
                <Form.Label>First period (months)</Form.Label>
                <Form.Control type="number" min="0" step="1" required value={form.initialPeriodMonths} onChange={set('initialPeriodMonths')} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3" controlId="pat-trailing-rate">
                <Form.Label>Rate after that (%)</Form.Label>
                <Form.Control type="number" min="0" max="100" step="0.01" required value={form.trailingRatePercent} onChange={set('trailingRatePercent')} />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3" controlId="pat-cap-amount">
                <Form.Label>Cap (amount)</Form.Label>
                <Form.Control type="number" min="0" step="0.01" placeholder="None" value={form.capAmount} onChange={set('capAmount')} />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3" controlId="pat-cap-months">
                <Form.Label>Stops after (months)</Form.Label>
                <Form.Control type="number" min="0" step="1" placeholder="Never" value={form.capMonths} onChange={set('capMonths')} />
              </Form.Group>
            </Col>
          </Row>

          <h6 className="mt-2">Changes so far</h6>
          {changes.length === 0 ? (
            <p className="text-muted small mb-0">No changes since this was assigned.</p>
          ) : (
            <Table size="sm" bordered responsive className="mb-0">
              <thead>
                <tr><th>When</th><th>By</th><th>First period</th><th>After</th><th>Cap</th></tr>
              </thead>
              <tbody>
                {changes.map((c) => (
                  <tr key={c.id}>
                    <td>{c.changedAt ? formatDateTime(c.changedAt) : ''}</td>
                    <td>{c.changedByEmail || '-'}</td>
                    <td>{c.oldInitialRatePercent}% for {c.oldInitialPeriodMonths} mo &rarr; {c.newInitialRatePercent}% for {c.newInitialPeriodMonths} mo</td>
                    <td>{c.oldTrailingRatePercent}% &rarr; {c.newTrailingRatePercent}%</td>
                    <td>{show(c.oldCapAmount)}{c.oldCapMonths != null ? ` / ${c.oldCapMonths} mo` : ''} &rarr; {show(c.newCapAmount)}{c.newCapMonths != null ? ` / ${c.newCapMonths} mo` : ''}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>Cancel</Button>
          <Button variant="primary" type="submit" disabled={saving}>{saving ? 'Saving...' : 'Change from today'}</Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export default PartnerAssignmentTermsModal;
